import { ChartData } from '../types';
import { GroupedByPayerCategory } from './payerCategoryGrouping';
import { registerPayerCategoryLabels } from './categoryColors';
import { createTranslateWithFallback } from '../../../../utils/translate';

/**
 * SHER-900. The grouped card's categories arrive as payer-category KEYS, which are stable and
 * language-independent — that is what makes the colour map and the tests work — so they are
 * translated here, at the last moment before they become chart labels.
 *
 * A category with no translation is left as its key rather than blanked: a wedge with no label
 * is unreadable, and the raw key at least says which one it is.
 *
 * Which vocabulary a chart holds is read from the tag its producer set, never inferred from the
 * strings: `IPM` is both a payer category and an insurer code several tenants really use, so an
 * un-categorised tenant's pie would otherwise get that one wedge translated and the rest left as
 * raw codes.
 */
export const payerCategoryLabels = (
  data: ChartData & { categoryKind?: 'payerCategory' },
  translate: (id: string) => any,
): ChartData | GroupedByPayerCategory => {
  if (data.categoryKind !== 'payerCategory') return data;

  const translateWithFallback = createTranslateWithFallback(translate);
  const labels: Record<string, string> = {};
  data.categories.forEach((category: string) => {
    labels[category] = translateWithFallback(`hospital.analytics.payer_category.${category}`, category);
  });

  // The pie resolves its colours from the rendered label, so the map has to learn them or the
  // same category changes colour when the language does.
  registerPayerCategoryLabels(labels);

  return { ...data, categories: data.categories.map((c: string) => labels[c] ?? c) };
};
